import { useMemo, useState } from "react"
import { ComboboxField } from "@ui/forms"
import { ControllerRenderProps, FieldValues, Path } from "react-hook-form"

import { useFetchContact, useFetchContacts } from "@/hooks/api/contacts"

interface ContactSelectorFieldProps<T extends FieldValues> {
  field: ControllerRenderProps<T, Path<T>>
  label?: string
  placeholder?: string
  currentContactId?: string
  bookIdOverride?: string
}

export function ContactSelectorField<T extends FieldValues>({
  field,
  label = "Contact",
  placeholder = "Select contact",
  currentContactId,
  bookIdOverride,
}: ContactSelectorFieldProps<T>) {
  const [searchQuery, setSearchQuery] = useState("")
  const { data: contacts, isLoading } = useFetchContacts(searchQuery, bookIdOverride)
  const selectedContactId = typeof field.value === "string" && field.value.length > 0 ? field.value : undefined
  const { data: selectedContact } = useFetchContact(selectedContactId ?? null)

  const options = useMemo(() => {
    const baseOptions =
      contacts?.contacts
        ?.filter((contact) => contact.id !== currentContactId)
        .map((contact) => ({ value: contact.id, label: contact.person?.full_name || "Unnamed contact" })) ?? []

    if (!selectedContact || !selectedContactId || selectedContactId === currentContactId) {
      return baseOptions
    }

    if (baseOptions.some((option) => option.value === selectedContactId)) {
      return baseOptions
    }

    return [
      { value: selectedContact.id, label: selectedContact.person?.full_name || "Unnamed contact" },
      ...baseOptions,
    ]
  }, [contacts?.contacts, currentContactId, selectedContact, selectedContactId])

  return (
    <ComboboxField
      id="contact_id"
      label={label}
      field={field}
      onSearch={setSearchQuery}
      options={options}
      isSearching={isLoading}
      placeholder={isLoading ? "Loading..." : placeholder}
    />
  )
}
